import { useEditor } from '@craftjs/core';
import { useCallback, useRef } from 'react';

/**
 * Hook that provides callbacks to track node IDs before and after a drag operation.
 * When a new node is detected after drag ends, it automatically selects it.
 *
 * Usage:
 * - Call `captureNodesBeforeDrag()` on dragstart
 * - Call `selectNewNodeAfterDrop()` on dragend
 */
export function useAutoSelectDroppedNode() {
  const { actions, query } = useEditor();
  const nodeIdsBeforeDragRef = useRef<Set<string> | null>(null);

  /**
   * Capture the current set of node IDs before drag starts.
   * Call this in the dragstart event handler.
   */
  const captureNodesBeforeDrag = useCallback(() => {
    const nodes = query.getSerializedNodes();
    nodeIdsBeforeDragRef.current = new Set(Object.keys(nodes));
  }, [query]);

  /**
   * Compare current node IDs with the captured set and select any newly added node.
   * Call this in the dragend event handler.
   * Uses a small delay to ensure Craft.js has finished processing the drop.
   */
  const selectNewNodeAfterDrop = useCallback(() => {
    // Use requestAnimationFrame to ensure Craft.js has finished processing the drop
    requestAnimationFrame(() => {
      const nodesBefore = nodeIdsBeforeDragRef.current;
      if (!nodesBefore) return;

      const nodes = query.getSerializedNodes();
      const currentNodeIds = Object.keys(nodes);

      // Find the newly added node ID
      const newNodeId = currentNodeIds.find((id) => !nodesBefore.has(id));

      if (newNodeId) {
        // Select the newly added node
        actions.selectNode(newNodeId);
      }

      // Clear the captured state
      nodeIdsBeforeDragRef.current = null;
    });
  }, [actions, query]);

  return {
    captureNodesBeforeDrag,
    selectNewNodeAfterDrop,
  };
}
